// frontend/src/pages/ResetPassword.jsx
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { useLanguage } from '../utils/i18n';
import LanguageToggle from '../components/LanguageToggle';
import Logo from '../components/Logo';
import { ArrowLeft } from 'lucide-react';

export default function ResetPassword() {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    // When coming from email, supabase client automatically processes the hash token
    // and establishes a temporary session. We will rely on that session handling during updateUser.
    useEffect(() => {
        supabase.auth.onAuthStateChange(async (event, session) => {
            if (event == "PASSWORD_RECOVERY") {
                // Good state
            }
        })
    }, []);

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');

        if (password.length < 8) {
            setError(t('err_pw_length'));
            return;
        }
        if (password !== confirmPassword) {
            setError(t('err_pw_match'));
            return;
        }

        setLoading(true);

        try {
            const { error: updateErr } = await supabase.auth.updateUser({ password: password });
            if (updateErr) throw updateErr;

            setSuccess(true);
            setTimeout(() => {
                // Sign them out of the temp session so they can securely grab a fresh login
                supabase.auth.signOut().then(() => {
                    navigate('/login');
                });
            }, 2000);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)', position: 'relative' }}>
            {/* Responsive Header */}
            <header className="header-container" style={{ position: 'relative', zIndex: 10 }}>
                <Link to="/" className="btn btn-outline glass-btn header-back-minimal" style={{ textDecoration: 'none' }}>
                    <ArrowLeft size={18} /> <span className="hide-mobile" style={{ marginLeft: 8 }}>{t('back_to_home')}</span>
                </Link>

                <div className="header-centered-logo">
                    <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
                        <Logo size={24} />
                        <span style={{ fontWeight: 800, fontSize: 18, color: 'var(--text)', letterSpacing: -0.5 }}>Wafiyo</span>
                    </Link>
                </div>

                <LanguageToggle />
            </header>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', position: 'relative' }}>
                <div className="bg-shapes hide-mobile">
                    <div className="shape shape-1" style={{ top: '-10%', left: '-10%', width: 600, height: 600, opacity: 0.15 }}></div>
                    <div className="shape shape-2" style={{ bottom: '-10%', right: '-10%', width: 800, height: 800, opacity: 0.1 }}></div>
                </div>

                <div className="glass-card" style={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
                    <div style={{ textAlign: 'center', marginBottom: 28 }}>
                        <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>{t('reset_pw_title')}</h1>
                        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>
                            {t('reset_pw_subtitle')}
                        </p>
                    </div>

                    {error && <div className="alert alert-error">{error}</div>}

                    {success ? (
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#10b981', marginBottom: 12 }}>
                                {t('reset_pw_success')}
                            </h2>
                            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
                                {t('redirecting_login')}
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} noValidate>
                            <div className="form-group">
                                <label>{t('reset_pw_label')}</label>
                                <div className="password-wrapper">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        required
                                    />
                                    <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex="-1">
                                        {showPassword ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>{t('reset_pw_confirm')}</label>
                                <div className="password-wrapper">
                                    <input
                                        type={showConfirm ? "text" : "password"}
                                        value={confirmPassword}
                                        onChange={e => setConfirmPassword(e.target.value)}
                                        placeholder="••••••••"
                                        required
                                    />
                                    <button type="button" className="password-toggle" onClick={() => setShowConfirm(!showConfirm)} tabIndex="-1">
                                        {showConfirm ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <button className="btn btn-primary btn-glow" type="submit" disabled={loading} style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>
                                {loading ? t('please_wait') : t('reset_pw_update_btn')}
                            </button>
                        </form>
                    )}
                </div>
            </div>

            <footer className="landing-footer" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-brand">
                            <Logo size={28} />
                            <span className="logo-text">Wafiyo</span>
                            <p className="footer-tagline">
                                {t('footer_tagline')}
                            </p>
                        </div>
                    </div>
                    <div className="footer-bottom">
                        <p>© {new Date().getFullYear()} Wafiyo. {t('all_rights_reserved')}</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
